// ── frontend/src/config/http.js ─────────────────────────────────────
// Hardening de identidad v49a: único lugar que resuelve el token del API y lo
// adjunta a cada llamada saliente.
//
//   - Interceptor global de axios: cubre las ~110 llamadas axios.* sin tocar
//     un solo call site.
//   - apiFetch(url, options): mismo contrato que fetch, agrega el header.
//   - Interceptor de respuesta: ante 401 renueva el token una vez y reintenta;
//     si vuelve a fallar, manda a re-autenticar.
//
// Se importa una vez en index.js para instalar el interceptor antes de la
// primera llamada (spec §7.6).
// ─────────────────────────────────────────────────────────────────────
import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, apiRequest } from '../authConfig';
import { API } from './api';

// Adjuntamos el token SOLO a llamadas a nuestro propio API. Nunca a terceros:
// mandar el Bearer a otro host filtraría el token.
function esLlamadaAlApi(url) {
    if (!url) return false;
    return url.startsWith(API) || url.startsWith('/api') || url.includes('jya-sistema-api.azurewebsites.net');
}

// Resuelve el access token del API. Silencioso por default; si Azure AD exige
// interacción, cae a redirect (spec §7.6).
export async function obtenerToken() {
    const cuenta = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!cuenta) return null;
    try {
        const r = await msalInstance.acquireTokenSilent({ ...apiRequest, account: cuenta });
        return r.accessToken;
    } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
            await msalInstance.acquireTokenRedirect(apiRequest);
            return null;   // el redirect recarga la app; no seguimos
        }
        throw err;
    }
}

// ── Interceptor de request: adjunta Authorization ────────────────────
axios.interceptors.request.use(async (config) => {
    if (esLlamadaAlApi(config.url)) {
        const token = await obtenerToken();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
});

// ── Interceptor de response: 401 → renovar y reintentar una vez ──────
axios.interceptors.response.use(
    (respuesta) => respuesta,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;

        if (status === 401 && original && !original._reintentado && esLlamadaAlApi(original.url)) {
            original._reintentado = true;
            try {
                const token = await obtenerToken();
                if (token) {
                    original.headers = original.headers || {};
                    original.headers.Authorization = `Bearer ${token}`;
                    return axios(original);
                }
            } catch (_) { /* cae a re-autenticar */ }
            await msalInstance.acquireTokenRedirect(apiRequest);
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

// ── apiFetch: reemplazo drop-in de fetch para llamadas al API ────────
export async function apiFetch(url, options = {}) {
    const headers = { ...(options.headers || {}) };
    if (esLlamadaAlApi(url)) {
        const token = await obtenerToken();
        if (token) headers.Authorization = `Bearer ${token}`;
    }
    return fetch(url, { ...options, headers });
}
